import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/components/ui/use-toast";
import { useAuth } from "@/providers/AuthProvider";

export const useOrganizationSwitcher = () => {
  const [isLoading, setIsLoading] = useState(false);
  const { user, refreshUserData } = useAuth();

  const switchOrganization = async (organizationId: string): Promise<boolean> => {
    if (!user?.id) {
      toast({
        title: "Error",
        description: "You must be logged in to switch organizations",
        variant: "destructive",
      });
      return false;
    }

    setIsLoading(true);
    try {
      console.log(`Switching user ${user.id} to organization ${organizationId}`);

      // First check if the relation already exists
      const { data: existingRelation, error: checkError } = await supabase
        .from('user_organization')
        .select('*')
        .eq('user_uuid', user.id)
        .eq('organization_uuid', organizationId)
        .maybeSingle();

      if (checkError) {
        console.error("Error checking existing relation:", checkError);
        throw checkError;
      }

      // If relation doesn't exist, create it
      if (!existingRelation) {

        // First we push the new releation : 

        const { error: insertError } = await supabase
          .from('user_organization')
          .insert({
            user_uuid: user.id,
            organization_uuid: organizationId
          });

        if (insertError) {
          console.error("Error creating new organization relation:", insertError);
          throw insertError;
        }
        
        // Then we remove any existing organization relations for this user except the one we just created
        const { error: deleteError } = await supabase
          .from('user_organization')
          .delete()
          .eq('user_uuid', user.id)
          .neq('organization_uuid', organizationId);

        if (deleteError) {
          console.error("Error removing existing organization relation:", deleteError);
          throw deleteError;
        }
        
      }

      // Refresh user data in the auth context
      await refreshUserData();

      toast({
        title: "Success",
        description: "Organization switched successfully",
      });
      
      return true;
    } catch (error: any) {
      console.error("Error switching organization:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to switch organization",
        variant: "destructive",
      });
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    switchOrganization,
    isLoading
  };
};
